import React, { useRef, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { inputClass, inputStyle, textareaClass } from '@/lib/styleKit'
import VariableInsertButton from './VariableInsertButton'
import VariableAutocomplete from './VariableAutocomplete'
import AIWritingToolbar from './AIWritingToolbar'

interface TemplateInputProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  rows?: number
  maxLength?: number
  className?: string
  multiline?: boolean
  aiAssist?: boolean
  aiFieldType?: 'subject' | 'body'
  /**
   * Id applied to the inner input/textarea — the actual control, not the
   * toolbar wrapper — so an external `<label htmlFor>` names it. `ui/Field`
   * supplies this automatically (§56).
   */
  id?: string
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}

export default function TemplateInput({
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
  className = '',
  multiline = true,
  aiAssist = false,
  aiFieldType,
  id,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: TemplateInputProps) {
  const inputRef = useRef<HTMLTextAreaElement & HTMLInputElement>(null)

  const handleInsert = useCallback((token: string) => {
    const el = inputRef.current
    if (!el) {
      onChange(value + token)
      return
    }
    // selectionStart/selectionEnd are 0 (falsy) when the cursor sits at the
    // very start of the field — `|| value.length` would wrongly treat that
    // as "no position" and insert at the end instead of the beginning.
    const start = el.selectionStart ?? value.length
    const end = el.selectionEnd ?? value.length
    const newVal = value.substring(0, start) + token + value.substring(end)
    onChange(newVal)
    setTimeout(() => {
      const pos = start + token.length
      el.setSelectionRange(pos, pos)
      el.focus()
    }, 0)
  }, [value, onChange])

  return (
    <div className="relative">
      <div className="flex items-center justify-end mb-1 gap-1">
        {aiAssist && (
          <AIWritingToolbar
            textareaRef={inputRef}
            value={value}
            onChange={onChange}
            fieldType={aiFieldType || (multiline ? 'body' : 'subject')}
          />
        )}
        <VariableInsertButton onInsert={handleInsert} />
      </div>
      {multiline ? (
        <textarea
          ref={inputRef as React.RefObject<HTMLTextAreaElement>}
          id={id}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          rows={rows}
          maxLength={maxLength}
          className={cn(textareaClass, className)}
          style={inputStyle}
        />
      ) : (
        <input
          ref={inputRef as React.RefObject<HTMLInputElement>}
          id={id}
          aria-describedby={ariaDescribedBy}
          aria-invalid={ariaInvalid || undefined}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          maxLength={maxLength}
          className={cn(inputClass, className)}
          style={inputStyle}
        />
      )}
      <VariableAutocomplete
        textareaRef={inputRef}
        value={value}
        onChange={onChange}
      />
    </div>
  )
}
